import React, { useEffect, useState } from 'react';
import { DataTablePageEvent } from 'primereact/datatable';

import { SalaryHistoryDbType } from '../../../api/types/accountants/salary-history/salary-history.type';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import { SalaryHistorySideBar } from './components/salary-history-side-bar';
import { SalaryHistoryTable } from './components/salary-history-table';
import { getSalaryHistoryOperations } from './operations/get-salary-history-operations';

const DEFAULT_START = new Date(2025, 6, 1);
const DEFAULT_END = new Date(2025, 7, 1);

export const SalaryHistory = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [branchId, setBranchId] = useState<number | null>(null);

  const [salariesData, setSalariesData] = useState<SalaryHistoryDbType[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);
  const [tableLoading, setTableLoading] = useState(false);

  const fetchPage = (
    rangeStart: Date,
    rangeEnd: Date,
    selectedBranchId: number,
    pageOffset: number,
    pageLimit: number,
  ) =>
    getSalaryHistoryOperations(
      rangeStart,
      rangeEnd,
      selectedBranchId,
      pageOffset,
      pageLimit,
      setSalariesData,
      setTotal,
      setTableLoading,
    );

  useEffect(() => {
    // init default branch (backend requires branchId)
    if (branchId != null) return;
    const branches = globalSettingsBranches.branches ?? [];
    const firstValid =
      branches.find((b) => Number.isInteger(b.id) && b.id >= 1)?.id ?? null;
    if (firstValid != null) {
      setBranchId(firstValid);
    }
  }, [branchId]);

  useEffect(() => {
    if (branchId == null) return;
    fetchPage(DEFAULT_START, DEFAULT_END, branchId, 0, 25);
  }, [branchId]);

  const handleFiltersChange = (
    newStartDate: Date | null,
    newEndDate: Date | null,
    newBranchId: number | null,
  ) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setBranchId(newBranchId);

    if (newStartDate && newEndDate && newBranchId != null) {
      setOffset(0);
      fetchPage(newStartDate, newEndDate, newBranchId, 0, limit);
    }
  };

  const handlePageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);

    const rangeStart = startDate ?? DEFAULT_START;
    const rangeEnd = endDate ?? DEFAULT_END;
    if (branchId == null) return;
    fetchPage(rangeStart, rangeEnd, branchId, newOffset, newLimit);
  };

  const refreshCurrentPage = () => {
    const rangeStart = startDate ?? DEFAULT_START;
    const rangeEnd = endDate ?? DEFAULT_END;
    if (branchId == null) return;
    fetchPage(rangeStart, rangeEnd, branchId, offset, limit);
  };

  return (
    <div style={{ display: 'flex', gap: '16px', paddingTop: '16px' }}>
      <SalaryHistorySideBar
        startDate={startDate}
        endDate={endDate}
        branchId={branchId}
        onFiltersChange={handleFiltersChange}
      />
      <div style={{ flex: 1 }}>
        <SalaryHistoryTable
          salariesData={salariesData}
          totalRecords={total}
          first={offset}
          rows={limit}
          loading={tableLoading}
          onPage={handlePageChange}
          onRefresh={refreshCurrentPage}
          startDate={startDate}
          endDate={endDate}
          branchId={branchId}
        />
      </div>
    </div>
  );
};
